import { useEffect, useState } from 'react'
import { Wrench, PauseCircle, AlertTriangle, Coffee, Clock, Shield } from 'lucide-react'
import { maintenanceAPI } from '@/lib/api'
import { can } from '@/lib/permissions'

/* Onderhouds-overlay voor deelnemers.
   - Poll `/api/maintenance` elke 60s (+ meteen bij tab-focus).
   - Als `is_active` en de user is GEEN admin/superadmin → full-screen overlay.
   - Admins zien geen overlay, maar wel een kleine banner bovenaan zodat
     ze weten dat onderhoudsmodus AAN staat voor deelnemers.

   Bewust NIET blocking op login: iedereen kan nog inloggen, admins moeten
   nog kunnen werken. De overlay verschijnt pas na login. */

const PRESET_STYLE = {
  onderhoud:      { icon: Wrench,        color: '#B4FF39' },
  buiten_dienst:  { icon: Coffee,        color: '#7BCFFF' },
  lan_gepauzeerd: { icon: PauseCircle,   color: '#FFB57A' },
  beperkt:        { icon: AlertTriangle, color: '#FF7A6C' },
}

export default function MaintenanceGate({ user, children }) {
  const [state, setState] = useState(null)   // null = nog niet geladen

  useEffect(() => {
    let alive = true
    const laad = () => maintenanceAPI.get()
      .then(r => { if (alive) setState(r.data) })
      .catch(() => { if (alive) setState({ is_active: false }) })
    laad()
    const t = setInterval(laad, 60_000)
    const onVis = () => { if (document.visibilityState === 'visible') laad() }
    document.addEventListener('visibilitychange', onVis)
    return () => { alive = false; clearInterval(t); document.removeEventListener('visibilitychange', onVis) }
  }, [])

  const isAdmin = can(user?.role, 'manage_users')

  // Nog niet geladen? Toon gewoon de app — geen valse "onderhoud"-flash.
  if (state == null) return children

  // Admin ziet nooit de overlay, maar wel een banner als het aan staat.
  if (isAdmin) {
    return (
      <>
        {state.is_active && <AdminMaintenanceBanner state={state} />}
        {children}
      </>
    )
  }

  // Gewone user + actief → overlay.
  if (state.is_active) return <MaintenanceOverlay state={state} />

  return children
}

function AdminMaintenanceBanner({ state }) {
  const P = PRESET_STYLE[state.preset] || PRESET_STYLE.onderhoud
  const Icon = P.icon
  return (
    <div className="w-full text-xs font-mono px-4 py-1.5 flex items-center gap-2 border-b border-border"
         style={{ background: `${P.color}18`, color: P.color }}>
      <Icon className="h-3.5 w-3.5" />
      <span className="font-semibold uppercase tracking-widest">
        Onderhoudsmodus actief voor deelnemers
      </span>
      <span className="opacity-80">— {state.title}</span>
      <a href="/admin/maintenance"
         className="ml-auto underline underline-offset-2 opacity-80 hover:opacity-100">
        beheren
      </a>
    </div>
  )
}

function MaintenanceOverlay({ state }) {
  const P = PRESET_STYLE[state.preset] || PRESET_STYLE.onderhoud
  const Icon = P.icon
  const eta = state.eta_at ? new Date(state.eta_at) : null
  const etaStr = eta && !isNaN(eta)
    ? eta.toLocaleString('nl-BE', { dateStyle: 'short', timeStyle: 'short' })
    : null

  return (
    <div className="fixed inset-0 z-[9999] bg-background flex items-center justify-center p-6">
      <div className="max-w-xl w-full">
        <div className="border border-border rounded-lg bg-card p-8 sm:p-10 space-y-6 text-center"
             style={{ borderTop: `4px solid ${P.color}` }}>
          <div className="mx-auto w-16 h-16 rounded-full grid place-items-center"
               style={{ background: `${P.color}20`, color: P.color }}>
            <Icon className="h-8 w-8" />
          </div>
          <div>
            <h1 className="font-[Rajdhani] font-bold text-3xl tracking-tight">
              {state.title}
            </h1>
            <p className="text-muted-foreground mt-2 whitespace-pre-wrap">
              {state.message}
            </p>
          </div>
          {etaStr && (
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded border border-border text-xs font-mono">
              <Clock className="h-3.5 w-3.5" />
              Verwacht terug: {etaStr}
            </div>
          )}
          <div className="text-[11px] font-mono text-muted-foreground/70 border-t border-border pt-4">
            Deze pagina ververst automatisch elke 60 s.
          </div>
        </div>
      </div>
    </div>
  )
}
